// ============================================
// REALTIME EVENT ROUTER — PURE HELPERS
// ============================================
// Pure decision functions extracted from VoiceBotPanel.tsx's
// handleDataChannelMessage (Wave 4a). Each function takes plain inputs and
// returns a boolean / string / array — no refs, setters, or side effects.
//
// The router itself remains inline in VoiceBotPanel.tsx until a Playwright
// harness exists (Wave 0b), at which point Wave 4b can extract the full
// dispatcher with confidence.

import { LanguageCode } from '../config/language'
import { GOODBYE_PHRASES, includesPhraseBoundary } from '../utils/goodbye-phrases'

// ----- User goodbye detection -----------------------------------------------

// Utterances at or below this many words are treated as a bare farewell
// ("ביי ביי", "okay, bye") — a goodbye phrase anywhere closes the call.
const SHORT_FAREWELL_MAX_WORDS = 6
// In longer utterances, only a goodbye phrase within the trailing words
// counts as a farewell — a goodbye word buried mid-sentence means the user
// is still talking, so we must NOT end the call.
const TRAILING_FAREWELL_WORDS = 4

/**
 * True if the user's transcribed utterance is contextually a farewell that
 * should end the session — NOT merely any sentence that happens to contain
 * "bye". Guards against closing mid-call by mistake:
 *  - A question (contains "?"/"؟") is never a farewell — the user wants more.
 *  - In a long utterance the goodbye phrase must TRAIL the sentence, so
 *    "ביי, אבל קודם יש לי שאלה" (bye, but first I have a question) won't close.
 *  - Short utterances ("אוקיי, ביי ביי") still close as expected.
 */
export const detectUserGoodbye = (userMsg: string, language: LanguageCode): boolean => {
  const raw = userMsg.trim()
  if (!raw) return false
  // A question means the conversation is still going — don't end it.
  if (/[?؟]/.test(raw)) return false

  const phrases = GOODBYE_PHRASES[language] || GOODBYE_PHRASES['he']
  const lower = raw.toLowerCase()
  const words = lower.split(/\s+/)

  const matches = (text: string) =>
    phrases.some((phrase) => includesPhraseBoundary(text, phrase.toLowerCase()))

  // Bare farewell — a goodbye phrase anywhere in a short utterance closes.
  if (words.length <= SHORT_FAREWELL_MAX_WORDS) {
    return matches(lower)
  }
  // Long utterance — require the farewell to be the closing intent.
  const tail = words.slice(-TRAILING_FAREWELL_WORDS).join(' ')
  return matches(tail)
}

// ----- Bot call-end detection (residents auto-hang-up) ----------------------

// Strong, unambiguous sign-off phrases the BOT says ONLY when wrapping up the
// call. Deliberately narrower than the user GOODBYE_PHRASES set so a casual
// "תודה" inside an answer never triggers an early hang-up.
const BOT_CALL_END_PHRASES: Record<string, readonly string[]> = {
  he: ['השיחה הסתיימה', 'נסיים כאן', 'נסיים את השיחה', 'להתראות', 'יום נפלא', 'יום נעים', 'המשך יום נעים', 'המשך יום מצוין', 'להישמע'],
  en: ['goodbye', 'have a great day', 'have a wonderful day', 'take care', 'bye for now'],
  ru: ['до свидания', 'всего доброго', 'хорошего дня'],
  fr: ['au revoir', 'bonne journée', 'à bientôt'],
  ar: ['مع السلامة', 'إلى اللقاء', 'يوم سعيد'],
}

/**
 * True when the BOT's own transcript signals the call is over, so the widget
 * can hang up instead of waiting for the user. Mirrors detectUserGoodbye's
 * guards (questions never end the call; in long messages the sign-off must
 * TRAIL the sentence) but uses the narrower BOT_CALL_END_PHRASES set.
 */
export const detectBotCallEnd = (botMsg: string, language: LanguageCode): boolean => {
  const raw = botMsg.trim()
  if (!raw) return false
  // Still asking the user something — not a sign-off.
  if (/[?؟]/.test(raw)) return false

  const phrases = BOT_CALL_END_PHRASES[language] || BOT_CALL_END_PHRASES['he']
  const lower = raw.toLowerCase()
  const words = lower.split(/\s+/)

  const matches = (text: string) =>
    phrases.some((phrase) => includesPhraseBoundary(text, phrase.toLowerCase()))

  if (words.length <= SHORT_FAREWELL_MAX_WORDS) {
    return matches(lower)
  }
  const tail = words.slice(-TRAILING_FAREWELL_WORDS).join(' ')
  return matches(tail)
}

// ----- Bot-spoke-URL detection ---------------------------------------------

/**
 * Returns URL-shaped fragments the model accidentally read aloud
 * (used purely for warning logs — does not change behavior).
 */
export const extractSpokenUrlFragments = (botMsg: string): string[] => {
  const matches = botMsg.match(/https?:|www\.|\.com|booking|romitravel/gi)
  return matches ? Array.from(matches) : []
}

// ----- Insurance no-info recovery decision ---------------------------------

const INSURANCE_NO_INFO_PATTERN = /(אין\s+לי\s+כרגע\s+מידע|אין\s+לי\s+מידע|לא\s+מצאתי\s+מידע|לא\s+מוצא\s+מידע)/

/**
 * Decide whether to trigger the insurance "I have no info" recovery flow.
 *
 * Returns true only when:
 *  - industry is insurance,
 *  - the bot's last message claims no info,
 *  - we have a remembered last user question,
 *  - we haven't already attempted recovery for that exact user question.
 */
export const shouldRecoverInsuranceNoInfo = (
  industry: string,
  botMsg: string,
  lastUserMessage: string | null | undefined,
  alreadyRecoveredFor: string | null | undefined,
): boolean => {
  if (industry !== 'insurance') return false
  if (!INSURANCE_NO_INFO_PATTERN.test(botMsg)) return false
  if (!lastUserMessage) return false
  if (alreadyRecoveredFor === lastUserMessage) return false
  return true
}

interface InsuranceResult {
  title: string
  content: string
}

/**
 * Build the system-correction instruction sent back to the model after the
 * insurance no-info recovery search returns results.
 */
export const buildInsuranceRecoveryInstruction = (
  userQuestion: string,
  results: InsuranceResult[],
): string => {
  const formattedResults = results
    .map((r, idx) => `[Result ${idx + 1}] ${r.title}\n${r.content}`)
    .join('\n\n')
  return `SYSTEM CORRECTION: Relevant insurance KB results were found. You MUST answer the customer's previous question using only the results below. Do NOT say that no information exists. If multiple policies/companies are present, mention that briefly and ask one short clarifying question.\n\nCustomer question: ${userQuestion}\n\n${formattedResults}`
}

// ----- Realtime API error classification -----------------------------------

/**
 * True if a Realtime API error is a known non-fatal warning that should
 * NOT be surfaced to the user via setError().
 */
export const isInternalRealtimeApiWarning = (errorMsg: string): boolean => {
  if (!errorMsg) return false
  return (
    errorMsg.includes('truncated') ||
    errorMsg.includes('not found in conversation') ||
    errorMsg.includes('already shorter than') || // Audio buffer timing warnings
    errorMsg.includes('Audio content') ||
    errorMsg.includes('no active response') || // response.cancel when no response is active
    errorMsg.includes('already has an active response') // response.create while another is in progress
  )
}
